// webtop's page: a webtop of tiles (shortcuts, groups and widgets) for one address. This file only starts it; the
// work is done by the modules in lib/ (one concern each, listed below) and the widgets in widgets/.
//   lib/state.js        the webtop record, the selection, the viewer's role
//   lib/ui.js           api(), modal(), toast(), confirmDialog()
//   lib/stillness.js    the page held still behind a modal dialog, and under the notch
//   lib/geometry.js     sizes, the grid, the screen, clamp / place / layout (adaptive and auto) / snap, the tile orders
//   lib/tiles.js        tile kinds, targets, icons, opening a tile
//   lib/db.js           this browser's IndexedDB database: the icon cache, the webtops stored locally and their assets
//   lib/icons.js        this browser's icon cache, filled from the icon proxy
//   (webtops stored in this browser, a webtop's assets and More > Storage are the storage applet's, applets/storage)
//   lib/render.js       drawing the webtop and the selection
//   lib/menu.js         the context menu engine; lib/menus.js the webtop's and tiles' menus
//   lib/input.js        long press, dragging, selection boxes, keys on tiles; lib/resize.js a tile's size, by its corners
//   lib/overlay.js      what the full-screen overlays share (launcher, Widgets, Webtops, Settings, group panel)
//   lib/shortcuts.js    adding, duplicating and removing tiles, the shortcut dialog; lib/suggest.js its Opens suggestions
//   lib/groups.js       group tiles and the group panel
//   lib/background.js   background colour and image; lib/color.js the shared colour picker
//   lib/arrange.js      the Arrange menu: Auto Arrange, snap, align, Sort By
//   lib/webtop.js       new, rename, delete, export / import; lib/bookmarks.js browser bookmark files
//   lib/lock.js         passwords, ownership, visibility, the lock screen
//   lib/notch.js        Home and recent webtops, the launcher and voice buttons, the Recent Webtops column (the launcher
//                       is the Launcher applet's, applets/launcher; push to talk the Assistant applet's, applets/assistant)
//   lib/notch-panel.js  the panel its grip opens: the rail and its tabs, and what applets register with the notch
//   lib/activity.js     every fetch counted, shown as the fill in the notch's grip
//   lib/nav.js          opening another webtop in place (no page load), Back and Forward
//   lib/add.js          the Add section: picking what to add
//   lib/actions.js      the page's actions, which applets run by name (the assistant's answers)
//   lib/channel.js      the live channel to the server (socket.io), open from the start; the open webtop's room
//   lib/views.js        mounting targets (windows, sidebars) and the Views applets open in them
//   lib/applets.js      the applet runtime: installed applets, their lifecycle, the host behind @webtop/api
//   (signing in, the account and its Settings card are the user applet's, applets/user; the Applets overlay, Load
//   Applet, publishing and the Applets card are the Applets applet's, applets/applets)
// Every module keeps its declarations at the top level and its event wiring in init(), run here in order once all
// of them have loaded, so no module touches another before it exists.
import { WIDGETS, stylesReady } from './widgets/index.js';
import { connect } from './widgets/kit.js';
import { $webtop, API, currentRole, slug, webtop, setWebtop } from './lib/state.js';
import { api, modal, toast } from './lib/ui.js';
import { drawnSize, sizeKey } from './lib/geometry.js';
import { onWebtop } from './lib/tiles.js';
import { render } from './lib/render.js';
import { submenu } from './lib/menu.js';
import { colorMenu } from './lib/color.js';
import { renderLocked, renderNoWebtop } from './lib/lock.js';
import * as geometry from './lib/geometry.js';
import * as activity from './lib/activity.js';
import * as menu from './lib/menu.js';
import * as draw from './lib/render.js';
import * as menus from './lib/menus.js';
import * as color from './lib/color.js';
import * as background from './lib/background.js';
import * as stillness from './lib/stillness.js';
import * as overlay from './lib/overlay.js';
import * as groups from './lib/groups.js';
import * as shortcuts from './lib/shortcuts.js';
import * as input from './lib/input.js';
import * as lock from './lib/lock.js';
import * as notch from './lib/notch.js';
import * as notchPanel from './lib/notch-panel.js';
import * as nav from './lib/nav.js';
import * as add from './lib/add.js';
import * as channel from './lib/channel.js';
import * as views from './lib/views.js';
import * as applets from './lib/applets.js';

notch.openHome();
for (const m of [activity, geometry, menu, draw, menus, color, background, stillness, overlay, groups, shortcuts, input, lock, notch, notchPanel, nav, add, channel, views, applets]) m.init();

// What the widget modules use of the page (see widgets/kit.js).
connect({
  api, API: () => API, render, submenu, colorMenu, modal, toast, sizeOf: drawnSize, sizeKey, currentRole, onWebtop, webtop: () => webtop, widgets: WIDGETS,
  putAsset: applets.putAsset, assetSrc: applets.assetSrc, // the storage applet's
});
// A webtop opened before this one arrived (a notch button on a slow load, lib/nav.js) wins: api() then throws `stale`.
let movedOn = false;
try {
  if (!slug) renderNoWebtop();
  else {
    const [loaded] = await Promise.all([api('GET', API), stylesReady]);
    setWebtop(loaded);
    render();
  }
} catch (e) {
  if (e.stale) movedOn = true;
  else if (e.locked) renderLocked(); // the unlock dialog is already open (see api())
  else $webtop.textContent = `Could not load webtop: ${e.message}`;
}
// a ?q= or #section as well wins: the launcher (the Launcher applet's) opens last
if (!movedOn) applets.appletService(applets.LAUNCHER).then((l) => l.fromAddress()).catch(() => {});
// The installed applets come up once the webtop is drawn (onStartup ones; a widget's applet when its tile shows).
applets.start();
